import type { GSplatVector3Input } from '@/plugins/gsplat/effects/effectUtils'
import { writeVector3Array } from '@/plugins/gsplat/effects/effectUtils'
import type { GSplatParticleLifecycleEffectOptions } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import { GSplatParticleLifecycleEffect } from '@/plugins/gsplat/effects/GSplatParticleLifecycleEffect'
import {
  GSPLAT_PARTICLE_MOTION_DEFAULTS,
  GSPLAT_PARTICLE_MOTION_LIMITS,
} from '@/plugins/gsplat/effects/particleMotion'
import { createGSplatParticleMotionBounds } from '@/plugins/gsplat/effects/particleMotionBounds'

export interface GSplatFractalOrbitParticleEffectOptions
  extends Omit<GSplatParticleLifecycleEffectOptions, 'scatterScale'> {
  boundsMin?: GSplatVector3Input
  boundsMax?: GSplatVector3Input
  orbitScale?: number
  frequency?: number
  sweepWidth?: number
}

export const GSPLAT_FRACTAL_ORBIT_PARTICLE_DEFAULTS = {
  ...GSPLAT_PARTICLE_MOTION_DEFAULTS['fractal-orbit'],
} as const

const fractalOrbitMotionFieldGLSL = /* glsl */ `
uniform vec3 uParticleBoundsMin;
uniform vec3 uParticleBoundsMax;
uniform vec3 uParticleBoundsCenter;
uniform float uParticleOrbitScale;
uniform float uParticleOrbitFrequency;
uniform float uParticleOrbitSweepWidth;

float particleFractalOrbitLocalProgress(vec3 originalCenter, float scatterProgress) {
  vec3 boundsSize = max(uParticleBoundsMax - uParticleBoundsMin, vec3(0.000001));
  vec3 normalizedCenter = clamp(
    (originalCenter - uParticleBoundsMin) / boundsSize,
    vec3(0.0),
    vec3(1.0)
  );
  vec3 sweepCorner = vec3(
    uParticleBoundsMin.x,
    uParticleBoundsMax.y,
    uParticleBoundsMin.z
  );
  float cornerOrder = clamp(
    length((originalCenter - sweepCorner) / boundsSize) / 1.7320508,
    0.0,
    1.0
  );
  float sweepAxis = normalizedCenter.x;
  float endpointEnvelope = 4.0 * sweepAxis * (1.0 - sweepAxis);
  float printOrder = mix(sweepAxis, cornerOrder, endpointEnvelope * 0.35);
  float sweepWidth = max(uParticleOrbitSweepWidth, 0.000001);
  float scanStart = clamp(printOrder - sweepWidth * 0.5, 0.0, 1.0 - sweepWidth);
  float scanEnd = scanStart + sweepWidth;
  return smoothstep(scanStart, scanEnd, clamp(scatterProgress, 0.0, 1.0));
}

vec3 particleLifecycleOffset(vec3 originalCenter, float scatterProgress) {
  float effectScale = max(uEffectScale, 0.000001);
  vec3 local = (originalCenter - uParticleBoundsCenter) / effectScale;
  float frequency = uParticleOrbitFrequency;
  vec3 orbit = vec3(
    sin((local.y * 2.0 + local.z) * frequency),
    sin((local.z * 2.0 - local.x) * frequency),
    sin((local.x * 2.0 + local.y) * frequency)
  ) * uParticleOrbitScale * effectScale;
  vec3 targetCenter = uParticleBoundsCenter + orbit;
  float localProgress = particleFractalOrbitLocalProgress(originalCenter, scatterProgress);
  return (targetCenter - originalCenter) * localProgress;
}
`

const fractalOrbitMotionFieldWGSL = /* wgsl */ `
fn particleFractalOrbitLocalProgress(originalCenter: vec3f, scatterProgress: f32) -> f32 {
  let boundsSize = max(uniform.uParticleBoundsMax - uniform.uParticleBoundsMin, vec3f(0.000001));
  let normalizedCenter = clamp(
    (originalCenter - uniform.uParticleBoundsMin) / boundsSize,
    vec3f(0.0),
    vec3f(1.0)
  );
  let sweepCorner = vec3f(
    uniform.uParticleBoundsMin.x,
    uniform.uParticleBoundsMax.y,
    uniform.uParticleBoundsMin.z
  );
  let cornerOrder = clamp(
    length((originalCenter - sweepCorner) / boundsSize) / 1.7320508,
    0.0,
    1.0
  );
  let sweepAxis = normalizedCenter.x;
  let endpointEnvelope = 4.0 * sweepAxis * (1.0 - sweepAxis);
  let printOrder = mix(sweepAxis, cornerOrder, endpointEnvelope * 0.35);
  let sweepWidth = max(uniform.uParticleOrbitSweepWidth, 0.000001);
  let scanStart = clamp(printOrder - sweepWidth * 0.5, 0.0, 1.0 - sweepWidth);
  let scanEnd = scanStart + sweepWidth;
  return smoothstep(scanStart, scanEnd, clamp(scatterProgress, 0.0, 1.0));
}

fn particleLifecycleOffset(originalCenter: vec3f, scatterProgress: f32) -> vec3f {
  let effectScale = max(uniform.uEffectScale, 0.000001);
  let local = (originalCenter - uniform.uParticleBoundsCenter) / effectScale;
  let frequency = uniform.uParticleOrbitFrequency;
  let orbit = vec3f(
    sin((local.y * 2.0 + local.z) * frequency),
    sin((local.z * 2.0 - local.x) * frequency),
    sin((local.x * 2.0 + local.y) * frequency)
  ) * uniform.uParticleOrbitScale * effectScale;
  let targetCenter = uniform.uParticleBoundsCenter + orbit;
  let localProgress = particleFractalOrbitLocalProgress(originalCenter, scatterProgress);
  return (targetCenter - originalCenter) * localProgress;
}
`

/** 将粒子映射到确定性耦合几何轨道并沿同一路径重组的效果。 */
export class GSplatFractalOrbitParticleEffect extends GSplatParticleLifecycleEffect {
  readonly orbitScale: number
  readonly frequency: number
  readonly sweepWidth: number

  private readonly _boundsMin: ReturnType<typeof createGSplatParticleMotionBounds>['min']
  private readonly _boundsMax: ReturnType<typeof createGSplatParticleMotionBounds>['max']
  private readonly _boundsCenter: ReturnType<typeof createGSplatParticleMotionBounds>['center']
  private readonly _boundsMinArray = [0, 0, 0]
  private readonly _boundsMaxArray = [0, 0, 0]
  private readonly _boundsCenterArray = [0, 0, 0]

  constructor(options: GSplatFractalOrbitParticleEffectOptions = {}) {
    super({
      ...options,
      id: options.id ?? 'particle-fractal-orbit',
    })

    const defaults = GSPLAT_FRACTAL_ORBIT_PARTICLE_DEFAULTS
    const limits = GSPLAT_PARTICLE_MOTION_LIMITS['fractal-orbit']
    const bounds = createGSplatParticleMotionBounds(options, this.effectScale)
    this._boundsMin = bounds.min
    this._boundsMax = bounds.max
    this._boundsCenter = bounds.center
    this.orbitScale = normalizeRange(options.orbitScale, limits.orbitScale, defaults.orbitScale)
    this.frequency = normalizeRange(options.frequency, limits.frequency, defaults.frequency)
    this.sweepWidth = normalizeRange(options.sweepWidth, limits.sweepWidth, defaults.sweepWidth)
  }

  protected override getParticleMotionFieldGLSL(): string {
    return fractalOrbitMotionFieldGLSL
  }

  protected override getParticleMotionFieldWGSL(): string {
    return fractalOrbitMotionFieldWGSL
  }

  protected override updateEffect(effectTime: number): void {
    super.updateEffect(effectTime)
    this.setUniform('uParticleBoundsMin', writeVector3Array(this._boundsMin, this._boundsMinArray))
    this.setUniform('uParticleBoundsMax', writeVector3Array(this._boundsMax, this._boundsMaxArray))
    this.setUniform(
      'uParticleBoundsCenter',
      writeVector3Array(this._boundsCenter, this._boundsCenterArray)
    )
    this.setUniform('uParticleOrbitScale', this.orbitScale)
    this.setUniform('uParticleOrbitFrequency', this.frequency)
    this.setUniform('uParticleOrbitSweepWidth', this.sweepWidth)
  }
}

function normalizeRange(
  value: number | undefined,
  limits: { min: number; max: number },
  fallback: number
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return fallback
  }
  return Math.min(limits.max, Math.max(limits.min, value))
}
